import { useState, useEffect, useMemo } from 'react';
import { Eye, LogOut, Search, Shield, Users, Stethoscope, Ambulance, Building2, Hospital, LogIn } from 'lucide-react';
import { Modal, toast } from './UI';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import api from '../utils/api';

export const ROLE_META = {
  admin:        { label: 'Administrador', icon: Shield,      color: 'var(--blue)' },
  client:       { label: 'Cliente',       icon: Building2,   color: 'var(--purple)' },
  professional: { label: 'Profesional',   icon: Stethoscope, color: 'var(--accent)' },
  ambulance:    { label: 'Ambulancia',    icon: Ambulance,   color: 'var(--gold)' },
  hospital:     { label: 'Hospital',      icon: Hospital,    color: 'var(--warn)' },
};

const ROLE_FILTERS = ['admin', 'client', 'professional', 'ambulance', 'hospital'];

function initials(u) {
  return `${(u.first_name || '?')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase();
}

// Modal del SuperAdmin para entrar en la app como cualquier usuario real.
export function ImpersonationPicker({ open, onClose, initialRole = null }) {
  const { startImpersonation } = useAuth();
  const [q, setQ] = useState('');
  const [role, setRole] = useState(initialRole);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entering, setEntering] = useState(null);
  const debouncedQ = useDebounce(q, 300);

  useEffect(() => { if (open) setRole(initialRole); }, [open, initialRole]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.saImpersonationTargets({ q: debouncedQ.trim(), role })
      .then(d => { if (!cancelled) setUsers(d.users || []); })
      .catch(e => { if (!cancelled) toast.error('No se pudieron cargar los usuarios', e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, debouncedQ, role]);

  const groups = useMemo(() => {
    const filtered = role ? users.filter(u => u.role === role) : users;
    const map = new Map();
    for (const u of filtered) {
      const key = u.tenant_name || 'Sin empresa';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    }
    return [...map.entries()];
  }, [users, role]);

  const enter = async (u) => {
    setEntering(u.id);
    try { await startImpersonation(u); }
    catch (e) { toast.error('No se pudo entrar como este usuario', e.message); setEntering(null); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Ver la app como…" subtitle="Entra como un usuario real para probar su vista y sus permisos. La sesión dura 1 hora y queda registrada en auditoría." icon={Eye} size="md">
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, email o empresa" autoFocus style={{ paddingLeft: 36 }} />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <button type="button" className={`imp-chip ${!role ? 'active' : ''}`} onClick={() => setRole(null)}>
          <Users size={13} /> Todos
        </button>
        {ROLE_FILTERS.map(r => {
          const M = ROLE_META[r];
          return (
            <button type="button" key={r} className={`imp-chip ${role === r ? 'active' : ''}`} onClick={() => setRole(r)}>
              <M.icon size={13} /> {M.label}
            </button>
          );
        })}
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto', margin: '0 -4px', padding: '0 4px' }}>
        {loading && users.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Cargando usuarios…</div>
        ) : groups.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No hay usuarios activos con ese filtro.</div>
        ) : groups.map(([tenant, list]) => (
          <div key={tenant} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-dim)', margin: '0 0 6px 2px' }}>{tenant}</div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              {list.map((u, i) => {
                const M = ROLE_META[u.role] || { label: u.role, icon: Users, color: 'var(--text-muted)' };
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderTop: i ? '1px solid var(--border)' : 'none', background: 'var(--card)' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: M.color, background: `color-mix(in srgb, ${M.color} 12%, transparent)` }}>
                      {initials(u)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.first_name} {u.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: M.color, flexShrink: 0 }}>
                      <M.icon size={12} /> {M.label}
                    </span>
                    <button type="button" className="imp-enter" disabled={!!entering} onClick={() => enter(u)}>
                      <LogIn size={13} /> {entering === u.id ? 'Entrando…' : 'Entrar'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// Franja fija mientras el SuperAdmin está viendo la app como otro usuario.
export function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useAuth();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!impersonation) return;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [impersonation]);
  if (!impersonation) return null;

  const M = ROLE_META[impersonation.role] || { label: impersonation.role };
  const minsLeft = Math.max(0, Math.round(((impersonation.expires_at || now) - now) / 60000));
  return (
    <div className="imp-banner" role="status">
      <Eye size={15} style={{ flexShrink: 0 }} />
      <span className="imp-banner-text">
        Viendo como <strong>{impersonation.first_name} {impersonation.last_name}</strong>
        <span className="imp-banner-meta"> · {M.label}{impersonation.tenant_name ? ` · ${impersonation.tenant_name}` : ''} · {minsLeft} min</span>
      </span>
      <button type="button" onClick={stopImpersonation}>
        <LogOut size={13} /> Volver a SuperAdmin
      </button>
    </div>
  );
}
